import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '@cowoo/shared/ui-web';
import { ApiProblem, formatLogTime, formatTime } from '@cowoo/shared';
import { api } from '../../lib/api';
import { onAdminEvent } from '../../lib/sse';
import { Dialog } from '../../ui';

/**
 * The live device log of one terminal (10-web-admin § 15, protocol § 6.1) — for
 * debugging, behind a modal.
 *
 * Opening it sends `log_tail` with a backlog, so the last lines show at once;
 * while it stays open it renews every four minutes without one. The lines come
 * over SSE `terminal_log` and live only in this component: nothing is stored on
 * the server, and closing the modal drops them. The terminal ends the tail by
 * itself a few minutes after the last renewal, so a closed tab costs nothing.
 */

/** § 6.1 — one tail's length; a renewal comes well before it runs out. */
const TAIL_SECONDS = 300;
const RENEW_MS = 240_000;
/** How much of the terminal's 500-line buffer to show at once. */
const BACKLOG = 300;
/** The modal keeps at most this many lines; older ones scroll out. */
const KEEP = 2000;
/** No ack after this long: the terminal is probably offline. */
const ANSWER_MS = 10_000;

interface LogLine {
  ts: number;
  level: 'info' | 'warn' | 'fatal';
  msg: string;
}

interface Entry extends LogLine {
  key: string;
}

type Status =
  { kind: 'waiting' } | { kind: 'live'; until: number } | { kind: 'no_answer' } | { kind: 'unsupported' };

const LEVEL_CLASS: Record<LogLine['level'], string> = {
  info: 'text-ink',
  warn: 'text-warn-text',
  fatal: 'text-danger-text font-semibold',
};

export function TerminalLog({
  terminal,
  onClose,
}: {
  terminal: { id: string; label: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [dropped, setDropped] = useState(0);
  const [status, setStatus] = useState<Status>({ kind: 'waiting' });
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [copied, setCopied] = useState(false);
  const pending = useRef(new Set<string>());
  const seen = useRef(new Set<string>());
  const list = useRef<HTMLDivElement>(null);
  const follow = useRef(true);

  // Lines and acks for this terminal; a second viewer's backlog arrives here
  // too, which the keys de-duplicate.
  useEffect(() => {
    const stopLog = onAdminEvent('terminal_log', (data) => {
      const batch = data as { terminalId: string; lines: LogLine[]; dropped?: number };
      if (batch.terminalId !== terminal.id) return;
      if (batch.dropped) setDropped((count) => count + (batch.dropped ?? 0));
      const fresh: Entry[] = [];
      for (const line of batch.lines) {
        const key = `${line.ts}|${line.level}|${line.msg}`;
        if (seen.current.has(key)) continue;
        seen.current.add(key);
        fresh.push({ ...line, key });
      }
      if (fresh.length === 0) return;
      setEntries((current) => {
        const next = [...current, ...fresh].sort((a, b) => a.ts - b.ts);
        const cut = next.length - KEEP;
        if (cut <= 0) return next;
        for (const entry of next.slice(0, cut)) seen.current.delete(entry.key);
        return next.slice(cut);
      });
    });
    const stopAck = onAdminEvent('terminal_ack', (data) => {
      const ack = data as {
        commandId: string;
        ok: boolean;
        code: string | null;
        data: { until?: number } | null;
      };
      if (!pending.current.delete(ack.commandId)) return;
      // Terminal software from before § 6.1 does not know the command.
      if (!ack.ok) return void (ack.code === 'unknown_type' && setStatus({ kind: 'unsupported' }));
      if (ack.data?.until) setStatus({ kind: 'live', until: ack.data.until * 1000 });
    });
    return () => {
      stopLog();
      stopAck();
    };
  }, [terminal.id]);

  // Start with a backlog, then keep the tail alive while the modal is open.
  useEffect(() => {
    let answerTimer: number | undefined;
    const tail = async (backlog: number): Promise<void> => {
      try {
        const { data, error } = await api.POST('/admin/terminals/{id}/commands', {
          params: { path: { id: terminal.id } },
          body: { type: 'log_tail', args: { seconds: TAIL_SECONDS, backlog } },
        });
        if (error) throw error;
        setProblem(null);
        pending.current.add(data.commandId);
        window.clearTimeout(answerTimer);
        answerTimer = window.setTimeout(() => {
          if (pending.current.has(data.commandId)) setStatus({ kind: 'no_answer' });
        }, ANSWER_MS);
      } catch (error) {
        setProblem(error instanceof ApiProblem ? error : null);
      }
    };
    void tail(BACKLOG);
    const renew = window.setInterval(() => void tail(0), RENEW_MS);
    return () => {
      window.clearInterval(renew);
      window.clearTimeout(answerTimer);
    };
  }, [terminal.id]);

  // Stay at the bottom while the reader is there; scrolling up stops that.
  useEffect(() => {
    const element = list.current;
    if (element && follow.current) element.scrollTop = element.scrollHeight;
  }, [entries]);

  const copy = (): void => {
    const text = entries.map((entry) => `${formatLogTime(entry.ts)} ${entry.level} ${entry.msg}`).join('\n');
    void navigator.clipboard?.writeText(text).then(() => setCopied(true));
  };

  return (
    <Dialog
      wide
      title={t('admin.terminals.log.title', { label: terminal.label })}
      intro={
        status.kind === 'live'
          ? t('admin.terminals.log.live', { time: formatTime(new Date(status.until).toISOString()) })
          : status.kind === 'no_answer'
            ? t('admin.terminals.log.noAnswer')
            : status.kind === 'unsupported'
              ? t('admin.terminals.log.unsupported')
              : t('admin.terminals.log.waiting')
      }
      confirmLabel={copied ? t('admin.terminals.copied') : t('admin.terminals.copy')}
      onConfirm={copy}
      disabled={entries.length === 0}
      cancelLabel={t('common.close')}
      onClose={onClose}
    >
      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
      <div
        ref={list}
        role="log"
        // The lines are a debug view, not something to read aloud as they arrive.
        aria-live="off"
        aria-label={t('admin.terminals.log.title', { label: terminal.label })}
        onScroll={(event) => {
          const element = event.currentTarget;
          follow.current = element.scrollHeight - element.scrollTop - element.clientHeight < 24;
        }}
        className="rounded-input border-line bg-fog h-[60vh] overflow-auto border px-3 py-2 font-mono text-xs leading-5"
        data-testid="terminal-log"
      >
        {dropped > 0 ? (
          <p className="text-muted">{t('admin.terminals.log.dropped', { count: dropped })}</p>
        ) : null}
        {entries.length === 0 ? (
          <p className="text-muted">{t('admin.terminals.log.empty')}</p>
        ) : (
          entries.map((entry) => (
            <p key={entry.key} className={`whitespace-pre-wrap break-all ${LEVEL_CLASS[entry.level]}`}>
              <span className="text-muted">{formatLogTime(entry.ts)}</span> {entry.msg}
            </p>
          ))
        )}
      </div>
      <p className="text-adminSmall text-muted">{t('admin.terminals.log.note')}</p>
    </Dialog>
  );
}
